import { useState } from "react";
import { toast } from "sonner";

export enum FileCategory {
  Document = "document",
  Image = "image",
  Any = "any",
}

const ALLOWED_FILE_TYPES = {
  pdf: "application/pdf",
  doc: "application/msword",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  jpg: "image/jpeg",
  png: "image/png",
} as const;

const FILE_TYPE_CONFIG: Record<
  FileCategory,
  { types: string[]; label: string }
> = {
  [FileCategory.Document]: {
    types: [
      ALLOWED_FILE_TYPES.pdf,
      ALLOWED_FILE_TYPES.doc,
      ALLOWED_FILE_TYPES.docx,
    ],
    label: "PDF or Word document (.pdf, .doc, .docx)",
  },
  [FileCategory.Image]: {
    types: [
      ALLOWED_FILE_TYPES.pdf,
      ALLOWED_FILE_TYPES.jpg,
      ALLOWED_FILE_TYPES.png,
    ],
    label: "PDF, JPG, or PNG file",
  },
  [FileCategory.Any]: {
    types: Object.values(ALLOWED_FILE_TYPES),
    label: "PDF, Word, JPG, or PNG file",
  },
};

const DEFAULT_FILE_CATEGORY = FileCategory.Document;
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

interface UseFileValidationOptions {
  category?: FileCategory;
  maxSizeBytes?: number;
}

export function useFileValidation(options: UseFileValidationOptions = {}) {
  const { category = DEFAULT_FILE_CATEGORY, maxSizeBytes = MAX_FILE_SIZE } =
    options;
  const [fileError, setFileError] = useState<string | undefined>(undefined);

  const validateAndSetFile = (
    file: File,
    onSuccess: (file: File) => void,
    onError?: (message: string) => void,
  ): boolean => {
    const { types, label } = FILE_TYPE_CONFIG[category];

    if (!types.includes(file.type)) {
      const message = `Please upload a ${label}`;
      setFileError(message);
      toast.error(message);
      onError?.(message);
      return false;
    }

    if (file.size > maxSizeBytes) {
      const sizeMB = Math.round(maxSizeBytes / (1024 * 1024));
      const message = `File size must be less than ${sizeMB}MB`;
      setFileError(message);
      toast.error(message);
      onError?.(message);
      return false;
    }

    setFileError(undefined);
    onSuccess(file);
    return true;
  };

  const clearError = () => setFileError(undefined);

  return { fileError, setFileError, clearError, validateAndSetFile };
}
